import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import AppFooter from "@/components/layout/AppFooter";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { getDefaultHomePath } from "@/lib/roles";

const LOGO_URL = "/smartdienstplan-logo.svg";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, resendConfirmation } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendingConfirmation, setResendingConfirmation] = useState(false);
  const [showResendOption, setShowResendOption] = useState(false);

  const EMAIL_CONFIRMATION_MESSAGE = "Bitte bestätige zuerst deine E-Mail-Adresse.";

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Bitte fülle alle Felder aus");
      return;
    }
    
    setLoading(true);
    setShowResendOption(false);
    try {
      const user = await login(email, password);
      toast.success(`Willkommen zurück, ${user.name}!`);
      navigate(getDefaultHomePath(user));
    } catch (error) {
      console.error("Login error:", error);
      const detail = error.response?.data?.detail || "Anmeldung fehlgeschlagen";
      toast.error(detail);
      if (detail === EMAIL_CONFIRMATION_MESSAGE) {
        setShowResendOption(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!email) {
      toast.error("Bitte gib zuerst deine E-Mail-Adresse ein.");
      return;
    }

    setResendingConfirmation(true);
    try {
      const response = await resendConfirmation(email);
      toast.success(response?.message || "Bestätigungslink wurde gesendet.");
    } catch (error) {
      console.error("Resend confirmation error:", error);
      toast.error(error.response?.data?.detail || "Bestätigungslink konnte nicht gesendet werden.");
    } finally {
      setResendingConfirmation(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col" data-testid="login-page">
      <div className="auth-container flex-1">
        {/* Image Side - Dark with Logo */}
        <div className="auth-image-side bg-[#212529]">
          <div className="absolute inset-0 bg-[#212529] flex items-center justify-center">
            <div className="text-center p-8">
              <img src={LOGO_URL} alt="smartdienstplan" className="w-32 h-32 mx-auto mb-8" />
              <h2 className="font-heading text-3xl font-bold text-white mb-4">Willkommen zurück!</h2>
              <p className="text-gray-400 text-lg max-w-md">
                Melde dich an, um deine Dienstpläne und Schichten zu sehen und mit deinem Team zusammenzuarbeiten.
              </p>
            </div>
          </div>
        </div>
        
        {/* Form Side - Light */}
        <div className="auth-form-side bg-[#f8fafc]">
          <div className="w-full max-w-md">
            <Link to="/" className="flex items-center gap-3 mb-8">
              <div className="w-8 h-8 bg-[#212529] rounded-lg flex items-center justify-center p-1">
                <img src={LOGO_URL} alt="smartdienstplan" className="w-full h-full" />
              </div>
              <span className="font-heading font-semibold text-xl text-[#212529]">smartdienstplan</span>
            </Link>
            
            <Card className="border-gray-200 bg-white shadow-lg">
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="font-heading text-2xl text-[#212529]">Anmelden</CardTitle>
                <CardDescription className="text-gray-500">
                  Gib deine Anmeldedaten ein, um fortzufahren
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#212529]">E-Mail</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="deine@email.de"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-11 border-gray-300 focus:border-[#17c671] focus:ring-[#17c671]/20"
                      data-testid="login-email-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-[#212529]">Passwort</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="h-11 pr-10 border-gray-300 focus:border-[#17c671] focus:ring-[#17c671]/20"
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-[#17c671] hover:bg-[#14a85d] text-white font-semibold btn-active"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Anmelden...
                      </>
                    ) : (
                      "Anmelden"
                    )}
                  </Button>
                  {showResendOption ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-11 border-gray-300"
                      onClick={handleResendConfirmation}
                      disabled={resendingConfirmation}
                      data-testid="resend-confirmation-btn"
                    >
                      {resendingConfirmation ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sende Link...
                        </>
                      ) : (
                        "Registrierungslink noch einmal senden"
                      )}
                    </Button>
                  ) : null}
                </form>
                <div className="mt-4 text-right text-sm">
                  <Link to="/forgot-password" className="text-[#17c671] hover:text-[#14a85d] font-medium">
                    Passwort vergessen?
                  </Link>
                </div>
                
                <div className="mt-6 text-center text-sm text-gray-500">
                  Noch kein Konto?{" "}
                  <Link to="/register" className="text-[#17c671] hover:text-[#14a85d] font-medium">
                    Jetzt registrieren
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <AppFooter />
    </div>
  );
};

export default LoginPage;
